import InfoIcon from '@mui/icons-material/Info';
import { Box, Typography, useTheme, Skeleton } from '@mui/material';
import { Alert } from '@mui/material';
import { useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';

import type { Annotation } from '@verantos-dev/core-annotation-tool';
import { Button } from '@verantos-dev/shared-components';
import { AttributeHeader } from '@verantos-dev/ui-annotation-tool';
import { AttributeRow } from '@verantos-dev/ui-annotation-tool';
import { ErrorAlertCard } from '@verantos-dev/ui-annotation-tool';

import { ShowEvidencesList } from './ShowEvidencesList';
import { useInProgressAnnotationCard } from './useInProgressAnnotationCard';

interface InProgressAnnotationCardProps {
  annotation: Annotation | null;
  conceptId: string;
  editingAnnotationId?: string;
  onCancelNew?: () => void;
  onSaveSuccess?: () => void;
}

export const InProgressAnnotationCard = ({
  annotation,
  conceptId,
  editingAnnotationId,
  onCancelNew,
  onSaveSuccess,
}: InProgressAnnotationCardProps) => {
  const {
    formAttributes,
    isAttributeVisible,
    getAttributeRule,
    handleChangeAttribute,
    isPresent,
    addEvidenceModeForConceptId,
    evidenceRequired,
    enableSaveButton,
    handleSaveAnnotation,
    startAnnotationMode,
    currentAnnotationId,
    isPresentNull,
    isSaveAttributesLoading,
    isSaveSpansLoading,
    editAnnotationError,
    closeEditAnnotationError,
    createAnnotationError,
    closeCreateAnnotationError,
    handleCancelEdit,
    handleCancelNewAnnotation,
    hasAnyAttributeValue,
  } = useInProgressAnnotationCard({ annotation, conceptId, editingAnnotationId, onSaveSuccess });
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const errorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if ((editAnnotationError || createAnnotationError) && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [editAnnotationError, createAnnotationError]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
      {formAttributes
        .filter((m) => isAttributeVisible(m.name))
        .map((attribute) => {
          const attributeRule = getAttributeRule(attribute.name);
          if (!attributeRule) return null;

          const isUnknown = attribute.isUnknown || false;
          const isDisabled = isUnknown;
          const isSupportedIndirectlyDisabled =
            attributeRule.type === 'boolean' && attribute.value === false;

          return (
            <Box key={attribute.name} sx={{ display: 'flex', flexDirection: 'column' }}>
              {isSaveAttributesLoading ? (
                <Skeleton variant="text" width="100%" height="60px" />
              ) : (
                <Box key={attribute.name} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <AttributeHeader name={attribute.name} required={attributeRule.required} />

                  <AttributeRow
                    attribute={attribute}
                    annotation={annotation}
                    isRequired={attributeRule.required}
                    attributeRule={attributeRule}
                    isDisabled={isDisabled}
                    isSupportedIndirectlyDisabled={isSupportedIndirectlyDisabled}
                    onChange={handleChangeAttribute}
                  />
                </Box>
              )}
            </Box>
          );
        })}

      {isPresent === false && !isPresentNull && !isSaveAttributesLoading && !isSaveSpansLoading ? (
        <Alert
          severity="info"
          iconMapping={{
            info: <InfoIcon sx={{ width: 22, height: 22 }} />,
          }}
          sx={{
            border: '1px solid',
            borderColor: 'info.main',
            alignItems: 'center',
            maxHeight: '140px',
            overflow: 'scroll',
            mt: theme.spacing(4),
            mb: theme.spacing(4),
            backgroundColor: theme.palette.blue[50],
            width: '100%',
            '& .MuiAlert-message': {
              width: '100%',
              flex: 1,
            },
          }}
        >
          <Typography variant="body2">
            {formatMessage({
              id: 'workspaceCommon.annotationConfig.evidenceNotRequiredWhenPresent',
            })}
          </Typography>
        </Alert>
      ) : (
        <ShowEvidencesList
          annotation={annotation}
          conceptId={conceptId}
          editingAnnotationId={editingAnnotationId}
          loading={isSaveSpansLoading || isSaveAttributesLoading}
          editEnabled
        />
      )}
      {evidenceRequired && !isSaveAttributesLoading && !isSaveSpansLoading && (
        <Alert
          severity="info"
          iconMapping={{
            info: <InfoIcon sx={{ width: 22, height: 22 }} />,
          }}
          sx={{
            border: '1px solid',
            borderColor: 'info.main',
            alignItems: 'center',
            maxHeight: '140px',
            overflow: 'scroll',
            backgroundColor: theme.palette.blue[50],
            width: '100%',
            '& .MuiAlert-message': {
              width: '100%',
              flex: 1,
            },
          }}
        >
          <Typography variant="body2">
            {formatMessage({ id: 'workspaceCommon.annotationConfig.evidenceRequiredWhenPresent' })}
          </Typography>
        </Alert>
      )}
      <Box ref={errorRef}>
        <ErrorAlertCard
          errorMessage={editAnnotationError ?? ''}
          onDismiss={closeEditAnnotationError}
        />
        <ErrorAlertCard
          errorMessage={createAnnotationError || ''}
          onDismiss={closeCreateAnnotationError}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {annotation ? (
          <Button
            id="close-edit-mode"
            variant="text"
            onClickHandler={handleCancelEdit}
            displayLabel={formatMessage({ id: 'workspaceCommon.annotationConfig.cancelEditMode' })}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              color: theme.palette.primary.main,
            }}
          />
        ) : hasAnyAttributeValue ? (
          <Button
            id="cancel-new-annotation"
            variant="text"
            onClickHandler={onCancelNew ?? handleCancelNewAnnotation}
            displayLabel={formatMessage({ id: 'workspaceCommon.annotationConfig.cancelEditMode' })}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              color: theme.palette.primary.main,
            }}
          />
        ) : (
          <Box></Box>
        )}

        <Box sx={{ display: 'flex', gap: theme.spacing(2), justifyContent: 'flex-end' }}>
          <Button
            id="add-evidence-button"
            displayLabel={
              addEvidenceModeForConceptId == `${conceptId}#${currentAnnotationId}`
                ? formatMessage({ id: 'workspaceCommon.annotationConfig.addingEvidenceButton' })
                : formatMessage({ id: 'workspaceCommon.annotationConfig.addEvidenceButton' })
            }
            variant="outlined"
            onClickHandler={() => startAnnotationMode(annotation)}
            disabled={
              addEvidenceModeForConceptId !== null ||
              isPresent === false ||
              isSaveAttributesLoading ||
              isSaveSpansLoading
            }
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              color: theme.palette.primary.main,
            }}
          />
          <Button
            id="save-annotation-button"
            displayLabel={formatMessage({
              id: 'workspaceCommon.annotationConfig.saveAnnotationButton',
            })}
            variant="contained"
            disabled={!enableSaveButton || isSaveAttributesLoading || isSaveSpansLoading}
            onClickHandler={handleSaveAnnotation}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              bgcolor: theme.palette.primary.dark,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
